import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid } from '@mui/material';

import {
    useChangeable,
    FullForm,
    BoolField,
    TextField,
    EmailField,
    PhoneField
} from '../CommonComponents/Form';

import { Popup, SectionDivider } from '../CommonComponents';

import {
    createUser,
    deleteUser,
    getUser,
    updateUser
} from '../../api/users';

import toast from '../../utils/toast';
import { UserType } from '../../types';
import { useSelector } from '../../redux/hooks';
import { selectUserProfile } from '../../redux/actions/users';

const defaultUser = {
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    phoneAlt: '',
    isAdmin: false,
    sendInvitation: false
};

const UserForm: React.FC = () => {
    const { id } = useParams();
    const [entity, onChange] = useChangeable(defaultUser);
    const navigate = useNavigate();
    const userProfile = useSelector(selectUserProfile());

    const [user, setUser] = React.useState<UserType | null>(null);

    const [showWatchlistCreationInfo, setShowWatchlistCreationInfo] = React.useState(false);
    const [showWatchlistDeletionInfo, setShowWatchlistDeletionInfo] = React.useState(false);
    const [createdUserId, setCreatedUserId] = React.useState<number | null>(null);

    React.useEffect(() => {
        (async () => {
            if (id) {
                const u = await getUser(id);
                onChange(u);
                setUser(u);
            }
        })();
    }, [id, onChange]);

    const onSubmit = React.useCallback(async () => {
        try {
            if (id) {
                await updateUser(id, entity);
                toast('Användare uppdaterad');
                navigate(-1);
            } else {
                const createdUser = await createUser(entity);
                toast('Användare skapad');
                setCreatedUserId(createdUser.id);
                navigate(`/users/${createdUser.id}`);
                setShowWatchlistCreationInfo(userProfile?.isCarrier || false);
            }
        } catch (e: any) {
            toast(e, `Kunde inte ${id ? 'uppdatera' : 'skapa'} användaren, försök igen`);
        }
    }, [id, entity, navigate, userProfile?.isCarrier]);

    const onDelete = React.useCallback(async () => {
        try {
            if (id) {
                await deleteUser(id);
                toast('Användare borttagen');
                setShowWatchlistDeletionInfo(userProfile?.isCarrier || false);
                navigate('/users');
            }
        } catch (e: any) {
            toast(e, 'Kunde inte ta bort användaren, försök igen');
        }
    }, [id, navigate, userProfile?.isCarrier]);

    return (
        <FullForm
            title={`${entity.id ? 'Redigera' : 'Skapa'} användare`}
            entity={entity}
            onSubmit={onSubmit}
            onDelete={entity.id ? onDelete : undefined}
            showBottomSave
        >
            <Popup
                open={showWatchlistCreationInfo}
                title="Skapade bevakningar"
                body={
                    'Bevakningar har skapats åt den nya användaren för alla '
                    + 'uppdrag och kapaciteter som publiceras i dina grupper.'
                }
                okLabel="Ok"
                handleOk={() => navigate(`/users/${createdUserId}`, { replace: true })}
            />
            <Popup
                open={showWatchlistDeletionInfo}
                title="Borttagna bevakningar"
                body="Samtliga bevakningar knutna till användaren har tagit bort."
                okLabel="Ok"
                handleOk={() => navigate('/users', { replace: true })}
            />
            <Grid container spacing={2}>
                <Grid item xs={12} lg={6}>
                    <SectionDivider label="Kontaktuppgifter" marginTop={1} />
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                name="firstName"
                                label="Förnamn"
                                value={entity.firstName}
                                onChange={onChange}
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                name="lastName"
                                label="Efternamn"
                                value={entity.lastName}
                                onChange={onChange}
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                name="title"
                                label="Titel"
                                value={entity.title}
                                onChange={onChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <EmailField
                                name="email"
                                label="E-post"
                                value={entity.email}
                                onChange={onChange}
                                required
                                helperText={
                                    user?.emailVerified && entity.email !== user?.email
                                        ? 'Användaren måste verifiera sin e-post igen om du ändrar den.'
                                        : ''
                                }
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <PhoneField
                                name="phone"
                                label="Mobil"
                                value={entity.phone}
                                onChange={onChange}
                                helperText={
                                    user?.phoneVerified && entity.phone && entity.phone !== user?.phone
                                        ? 'Användaren måste verifiera sitt mobilnummer igen om du ändrar det.'
                                        : ''
                                }
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                name="phoneAlt"
                                label="Telefon"
                                maxLength={20}
                                value={entity.phoneAlt}
                                onChange={onChange}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} lg={6}>
                    <SectionDivider label="Behörigheter" marginTop={1} />
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <BoolField
                                name="isAdmin"
                                label="Administratör"
                                value={entity.isAdmin}
                                onChange={onChange}
                                helperText="Administratörer kan hantera användare, grupper och företagsuppgifter."
                            />
                        </Grid>
                        {!id && (
                            <Grid item xs={12}>
                                <SectionDivider label="Övrigt" marginTop={2} />
                                <BoolField
                                    name="sendInvitation"
                                    label="Skicka inbjudan"
                                    value={entity.sendInvitation}
                                    onChange={onChange}
                                    helperText="Användaruppgifter skickas till e-postadressen när användaren skapats."
                                />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </FullForm>
    );
};

export default UserForm;
